<template>
  <component
    :is="as"
    ref="card"
    :style="{
      '--x': `${elementX}px`,
      '--y': `${elementY}px`,
      '--spotlight-color-stops': spotlightColorStops,
      '--spotlight-size': `${size}px`,
      '--radius': `${radius}px`,
    }"
    :class="
      twMerge([
        mode === 'before'
          && 'before:absolute before:inset-0 before:bg-[radial-gradient(var(--spotlight-size)_circle_at_var(--x)_var(--y),var(--spotlight-color-stops))]',
        mode === 'after'
          && 'after:absolute after:inset-0 after:bg-[radial-gradient(var(--spotlight-size)_circle_at_var(--x)_var(--y),var(--spotlight-color-stops))]',
        'relative transform-gpu overflow-hidden bg-gray-300/60 dark:bg-slate-700 rounded-[var(--radius)] p-8',
        appConfig.ui.card.shadow,
        $attrs.class as string,
      ])
    "
  >
    <div
      :class="
        twMerge([
          'absolute inset-px rounded-[calc(var(--radius)-1px)] ',
          backgroundClasses,
        ])
      "
    ></div>
    <div class="relative">
      <slot />
    </div>
  </component>
</template>

<script setup lang="ts">
import { useMouseInElement } from '@vueuse/core'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(
  defineProps<{
    as?: string
    backgroundClasses?: string
    from?: string
    hsl?: boolean
    hslMax?: number
    hslMin?: number
    mode?: 'after' | 'before'
    radius?: number
    size?: number
    to?: string
    via?: null | string
  }>(),
  {
    as: 'div',
    backgroundClasses: 'bg-white dark:bg-gray-800/90',
    from: 'rgba(255,255,255,0.8)',
    hsl: false,
    hslMax: 360,
    hslMin: 0,
    mode: 'before',
    radius: 20,
    size: 350,
    to: 'transparent',
    via: null,
  },
)

const appConfig = useAppConfig()

const card = ref()
const { elementHeight, elementWidth, elementX, elementY }
  = useMouseInElement(card)

const spotlightColorStops = computed(() => {
  if (props.hsl) {
    const margin = props.hslMax - props.hslMin
    const rate
      = (elementY.value + elementX.value)
        / (elementHeight.value + elementWidth.value)
    const hue = Math.round(margin * rate + props.hslMin)

    return `hsl(${hue} 80% 70%),transparent`
  }

  return [props.from, props.via, props.to].filter(value => !!value).join(',')
})
</script>
